<template>
  <v-container class="payment-page py-10">
    <h1 class="text-center display-1 font-weight-bold mb-10">{{ $t("choose-your-plan") }}</h1>

    <div class="plans-nav text-center mb-16">
      <a href="#one-time-plans" @click.prevent="scrollTo('one-time-plans')" class="mr-4 text--primary plans-nav-link v-btn">{{ $t("onetime-plans") }}</a>
      <a href="#subscription-plans" @click.prevent="scrollTo('subscription-plans')" class="text--primary plans-nav-link v-btn">{{ $t("subscription-plans") }}</a>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">{{ $t("loading-plans") }}</p>
    </div>

    <template v-else>
      <v-row>
        <v-col cols="12">
          <!-- One-Time Plans Section (Top Prominence) -->
          <v-divider class="mb-10" ></v-divider>
          <section id="one-time-plans" v-if="oneTimePlans.length > 0" class="mb-16">
            <h2 class="text-center headline mb-2">{{ $t("onetime-plans") }}</h2>
            <p class="text-center subtitle-1 mb-8 text--secondary" style="max-width: clamp(600px, 80%, 1000px); margin-inline: auto">
              {{ $t("onetime-plans-description") }}
            </p>
            <v-row justify="center">
              <v-col v-for="plan in oneTimePlans" :key="plan.id" cols="12" sm="6" md="6" lg="4" class="d-flex">
                <v-card class="plan-card flex-grow-1 d-flex flex-column featured-onetime" outlined elevation="2">
                  <v-card-title class="headline font-weight-bold justify-center pt-8 text-center px-4">
                    {{ plan.name }}
                  </v-card-title>

                  <v-card-text class="text-center flex-grow-1">
                    <div class="price-container mb-4">
                      <span class="currency">CHF</span>
                      <span class="amount">{{ formatAmount(plan.amount) }}</span>
                    </div>

                    <p class="plan-details mb-6 text--secondary">{{ plan.details }}</p>

                    <v-divider class="mb-6 mx-4"></v-divider>

                    <ul class="features-list text-left px-4">
                      <li v-for="(value, key) in plan.limits" :key="key" class="mb-3 d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                        <span class="feature-text">
                          <strong>{{ value }}</strong> {{ $t(key) }}
                        </span>
                      </li>
                    </ul>
                  </v-card-text>

                  <v-card-actions class="pb-8 px-6">
                    <v-btn block large @click="selectPlan(plan)" color="primary" rounded depressed>
                      {{ $t("buy-now") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </section>
        </v-col>
        <v-col cols="12">
          <v-divider class="mb-10" ></v-divider>
          <!-- Recurring Plans Section -->
          <section id="subscription-plans" v-if="recurringPlans.length > 0">
            <h2 class="text-center headline mb-2">{{ $t("subscription-plans") }}</h2>
            <p class="text-center subtitle-1 mb-8 text--secondary" style="max-width: clamp(600px, 80%, 1000px); margin-inline: auto">{{ $t("subscription-plans-description") }}</p>
            <v-row justify="center" align="stretch">
              <v-col v-for="plan in recurringPlans" :key="plan.id" cols="12" sm="6" md="6" lg="4" class="d-flex">
                <v-card class="plan-card flex-grow-1 d-flex flex-column" :class="{'popular-plan': plan.pg_plan_lookup === 'standard_monthly_plan'}" outlined>
                  <div v-if="plan.pg_plan_lookup === 'standard_monthly_plan'" class="popular-badge text-uppercase font-weight-bold">
                    {{ $t("most-popular") }}
                  </div>

                  <v-card-title class="headline font-weight-bold justify-center pt-8">
                    {{ plan.name }}
                  </v-card-title>

                  <v-card-text class="text-center flex-grow-1">
                    <div class="price-container mb-4">
                      <span class="currency">CHF</span>
                      <span class="amount">{{ formatAmount(plan.amount) }}</span>
                      <span class="period" v-if="plan.interval === 'month'">/{{ $t("month") }}</span>
                    </div>

                    <p class="plan-details mb-6 text--secondary">{{ plan.details }}</p>

                    <v-divider class="mb-6 mx-4"></v-divider>

                    <ul class="features-list text-left px-4">
                      <li v-for="(value, key) in plan.limits" :key="key" class="mb-3 d-flex align-center">
                        <v-icon small color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                        <span class="feature-text">
                          <strong>{{ value }}</strong> {{ $t(key) }}
                        </span>
                      </li>
                    </ul>
                  </v-card-text>

                  <v-card-actions class="pb-8 px-6">
                    <v-btn block large @click="selectPlan(plan)" color="primary" :outlined="plan.pg_plan_lookup !== 'standard_monthly_plan'" rounded depressed>
                      {{ $t("get-started") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </section>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script lang="ts">
import {Component, Vue} from "vue-property-decorator"
import plansData from "@/assets/plans/plans.json"

@Component
export default class PaymentPage extends Vue {
  plans: any[] = []
  loading = true
  language = localStorage.getItem("user-language") || "en"

  get recurringPlans() {
    return this.plans.filter((p) => p.type === "recurring")
  }

  get oneTimePlans() {
    return this.plans.filter((p) => p.type === "onetime")
  }

  formatAmount(amount: number) {
    if (amount === 0) return "0"
    return amount.toFixed(2).replace(".", ",")
  }

  async mounted() {
    try {
      this.language = localStorage.getItem("user-language") || "en"
      const response = await fetch(`${process.env.BASE_URL}plans/plans.json`)
      if (response.ok) {
        const data = await response.json()
        if (data && data.plans) {
          this.initPlans(data.plans)
        }
      }
    } catch (error) {
      console.warn("Using local plans data")
    } finally {
      this.loading = false
    }
  }

  initPlans(rawPlans: any[]) {
    const lang = this.language
    const processedPlans = rawPlans.map((plan) => {
      const p = {...plan}
      if (p.lang && p.lang[lang]) {
        p.name = p.lang[lang].name || p.name
        p.details = p.lang[lang].details || p.details
      }
      return p
    })
    Vue.set(this, "plans", processedPlans)
  }

  selectPlan(plan: any) {
    let planId = plan.id
    this.$router.push({name: "checkout", params: {planId: planId.toString()}})
  }

  scrollTo(id: string) {
    const el = document.getElementById(id)
    if (!el) return
    const headerEl = (this.$el as HTMLElement).querySelector("h1") as HTMLElement | null
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height + 16 : 80
    const top = window.pageYOffset + el.getBoundingClientRect().top - headerHeight - 8
    window.scrollTo({top, behavior: "smooth"})
  }
}
</script>

<style scoped>
.payment-page {
  max-width: 1200px;
  margin: 0 auto;
}

.plan-card {
  background-color: #fff !important;
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  overflow: visible;
  border: 1px solid #e0e0e0 !important;
}

.plan-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1) !important;
  border-color: #1976d2 !important;
}

.featured-onetime {
  border: 2px solid #1976d2 !important;
  background-color: #f8fbff !important;
}

.popular-plan {
  border: 2px solid #4caf50 !important;
}

.popular-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #4caf50;
  color: #fff;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 11px;
  letter-spacing: 0.5px;
  z-index: 2;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.price-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.currency {
  font-size: 1.1rem;
  font-weight: 600;
  margin-right: 2px;
  color: #444;
}

.amount {
  font-size: 3.2rem;
  font-weight: 900;
  color: #1a1a1a;
}

.period {
  font-size: 1rem;
  opacity: 0.8;
  margin-left: 2px;
  color: #666;
}

.features-list {
  list-style: none;
  padding: 0;
}

.feature-text {
  font-size: 0.95rem;
  color: #444;
}

.headline {
  font-weight: 700 !important;
  color: #1a1a1a;
}

.plans-nav-link {
  cursor: pointer;
  text-decoration: none;
  font-weight: 600;
}

section[id] {
  scroll-margin-top: 110px;
  padding-top: 6px;
}
</style>
